import "./birthday.scss";

import { MessageResponseDto } from "@api/api";
import AnimatedCounter from "@core/ui/animated-counter/animated-counter";
import { useQyClaimBirthdayReward } from "@factory/query/birthday.query";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import useScreenSize from "@shared/utility/screen-size";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { appFeature } from "../../app.feature";

type ClaimStatus = "idle" | "claiming" | "success" | "error";

interface ClaimResult {
  points?: number;
  stamp?: number;
  message?: string;
}

export function Birthday() {
  const { pointsText, stampsText } = appFeature.config.label;
  const { isMobileMode } = useScreenSize();
  const { showSuccess } = useNotificationContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const claimStarted = useRef(false);
  const [status, setStatus] = useState<ClaimStatus>(token ? "idle" : "error");
  const [claimResult, setClaimResult] = useState<ClaimResult>();

  const handleClaimSuccess = (response: MessageResponseDto) => {
    const data = response.data as ClaimResult | undefined;
    setClaimResult({
      points: data?.points,
      stamp: data?.stamp,
      message: response.message,
    });
    setStatus("success");
    showSuccess(response.message ?? "Birthday reward claimed!");
  };

  const handleClaimError = () => {
    setStatus("error");
  };

  const { mutate: claimBirthdayReward } = useQyClaimBirthdayReward(
    handleClaimSuccess,
    handleClaimError,
  );

  useEffect(() => {
    if (!token || claimStarted.current) {
      return;
    }

    claimStarted.current = true;
    setStatus("claiming");
    claimBirthdayReward({ token });
  }, [token]);

  const handleClose = () => {
    navigate("/customer");
  };

  const footerContent = (
    <div>
      <Button
        className="block w-full mt-2"
        label="Go to My Rewards"
        severity="secondary"
        outlined
        onClick={handleClose}
      />
    </div>
  );

  const points = claimResult?.points ?? 0;
  const stamp = claimResult?.stamp ?? 0;

  return (
    <div id="Birthday" className="birthday">
      <Dialog
        header="Birthday Reward"
        visible={true}
        style={{ width: "50vw" }}
        onHide={handleClose}
        closeOnEscape={true}
        footer={status === "success" ? footerContent : undefined}
        closable={status !== "claiming"}
        maximized={isMobileMode}
      >
        {status === "claiming" ? (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <ProgressSpinner />
            <p className="m-0 text-gray-600">Claiming your birthday reward...</p>
          </div>
        ) : null}

        {status === "success" ? (
          <div className="text-center">
            <h3>Happy Birthday!</h3>
            <p className="text-gray-600">
              {claimResult?.message ?? "Your birthday reward has been added to your account."}
            </p>
            {points || stamp ? (
              <>
                <h5 className="mt-6">You received</h5>
                <div className="flex justify-center gap-4 mt-4">
                  {points ? (
                    <AnimatedCounter
                      from={0}
                      to={points}
                      duration={1}
                      delay={0.5}
                      label={pointsText}
                    />
                  ) : null}
                  {points && stamp ? <Divider layout="vertical" /> : null}
                  {stamp ? (
                    <AnimatedCounter
                      from={0}
                      to={stamp}
                      duration={1}
                      delay={0.5}
                      label={stampsText}
                    />
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {status === "error" ? (
          <div className="text-center py-4">
            <i className="pi pi-exclamation-circle text-4xl text-red-500 mb-4" />
            <h3>Unable to Claim Reward</h3>
            <p className="text-gray-600">
              {token
                ? "This birthday reward link is invalid or has already been used."
                : "This link is missing a claim token. Please use the link from your birthday email."}
            </p>
            <Button
              className="mt-4"
              label="Go to My Rewards"
              severity="secondary"
              outlined
              onClick={handleClose}
            />
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

export default Birthday;
